// src/Dashboard/pages/teacher/GradeCN/GradeCNDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useGradeCNDashboard } from "./hooks/useGradeCNDashboard";

import GradeCNHeader from "./components/GradeCNHeader";
import GradeCNFiltersCard from "./components/GradeCNFiltersCard";
import GradeCNKpiCards from "./components/GradeCNKpiCards";
import GradeCNDataTable from "./components/GradeCNDataTable";
import GradeCNMobileCards from "./components/GradeCNMobileCards";
import GradeCNEmptyState from "./components/GradeCNEmptyState";
import GradeCNDrawer from "./components/GradeCNDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0,
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Grades Ceinture Noire
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function GradeCNDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);
  const authorized = roleId === 1 || roleId === 3;

  const {
    grades,
    filteredGrades,
    stats,
    filters,
    setFilters,
    seasons,
    belts,
    presentBeltNames,
    beltHistories,
    loading,
    saving,
    deletingId,
    drawerOpen,
    drawerMode,
    currentGrade,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateGrade,
    handleUpdateGrade,
    handleDeleteGrade,
    refresh,
  } = useGradeCNDashboard();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <GradeCNHeader onAdd={openCreateDrawer} />

        <GradeCNFiltersCard
          filters={filters}
          setFilters={setFilters}
          seasons={seasons}
          presentBeltNames={presentBeltNames}
          loading={loading}
          onAdd={openCreateDrawer}
          onRefresh={refresh}
        />

        <GradeCNKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste Grade CN
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Suivi des candidats à la ceinture noire
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Visualisez les UV validées, les points UV3, la dominante, la
                  ceinture actuelle et le prochain objectif.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Résultats :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredGrades.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {grades.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredGrades.length === 0 ? (
              <GradeCNEmptyState onAdd={openCreateDrawer} />
            ) : (
              <div className="mt-5">
                <GradeCNDataTable
                  grades={filteredGrades}
                  belts={belts}
                  beltHistories={beltHistories}
                  deletingId={deletingId}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteGrade}
                />

                <GradeCNMobileCards
                  grades={filteredGrades}
                  belts={belts}
                  beltHistories={beltHistories}
                  deletingId={deletingId}
                  onEdit={openEditDrawer}
                  onDelete={handleDeleteGrade}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <GradeCNDrawer
        isOpen={drawerOpen}
        mode={drawerMode}
        grade={currentGrade}
        saving={saving}
        onClose={closeDrawer}
        onSubmit={
          drawerMode === "create" ? handleCreateGrade : handleUpdateGrade
        }
      />

      <ToastContainer />
    </section>
  );
}
